// SAB-002a / SAB-002b — canonical legal-authority JURISDICTION applicability.
//
// One rule, one module. Every surface that renders or counts legal
// authorities (report chapters, review page, dashboard, PDF, evidence and
// conclusion counts) must filter through `filterAuthoritiesByJurisdiction`
// before it counts or displays anything.
//
// Rules (approved, SAB-002a/b):
//   • Federal authorities apply nationally.
//   • State authorities apply only when they match the resolved review state.
//   • Local authorities apply only when the locality matches (when locality
//     data exists on both sides); otherwise the state match governs.
//   • Authorities from another state are excluded everywhere.
//   • An UNRESOLVED jurisdiction never silently admits every authority —
//     only federal authorities survive, and callers must render the
//     explicit pending/unavailable state (`resolved === false`).
//
// This module is deterministic and performs no I/O.

import { classifyLegalTopics } from "@/lib/legal/topic-classifier";

export const STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

/** Normalize "OH" / "Ohio" / "ohio " to a two-letter code, or null. */
export function toStateCode(raw?: string | null): string | null {
  const v = (raw ?? "").trim();
  if (!v) return null;
  if (/^[A-Za-z]{2}$/.test(v) && STATE_NAMES[v.toUpperCase()]) return v.toUpperCase();
  const entry = Object.entries(STATE_NAMES).find(
    ([, name]) => name.toLowerCase() === v.toLowerCase(),
  );
  return entry ? entry[0] : null;
}

export function toStateName(raw?: string | null): string | null {
  const code = toStateCode(raw);
  return code ? STATE_NAMES[code] : null;
}

export interface AuthorityLike {
  citation?: string | null;
  statute?: string | null;
  title?: string | null;
  topic?: string | null;
  summary?: string | null;
  jurisdiction?: string | null;
  state?: string | null;
  why_it_matters?: string | null;
  potential_negotiation_impact?: string | null;
}

/** The jurisdiction an authority set is being evaluated against. */
export interface JurisdictionScope {
  /** Two-letter code, or null when unresolved. */
  state: string | null;
  /** City / municipality when known. */
  locality: string | null;
  /** False when the review's state could not be resolved. */
  resolved: boolean;
}

export function jurisdictionScope(
  stateRaw?: string | null,
  localityRaw?: string | null,
): JurisdictionScope {
  const state = toStateCode(stateRaw);
  const locality = (localityRaw ?? "").trim() || null;
  return { state, locality, resolved: state !== null };
}

const FEDERAL_RE = /\b(federal|u\.?s\.?c?\.?|united states|cfr|fmcsa|dot|ftc)\b/i;

export function isFederalAuthority(row: AuthorityLike): boolean {
  const j = `${row.state ?? ""} ${row.jurisdiction ?? ""}`.trim();
  if (j && FEDERAL_RE.test(j)) return true;
  const citation = `${row.citation ?? ""} ${row.statute ?? ""}`;
  return /\b(\d+\s*C\.?F\.?R\.?|\d+\s*U\.?S\.?C\.?)\b/i.test(citation);
}

/** The state an authority belongs to, or null when it names none. */
export function authorityState(row: AuthorityLike): string | null {
  const explicit = toStateCode(row.state) ?? toStateCode(row.jurisdiction);
  if (explicit) return explicit;
  const haystack = `${row.jurisdiction ?? ""} ${row.state ?? ""} ${row.citation ?? ""} ${row.statute ?? ""} ${row.title ?? ""}`;
  if (!haystack.trim()) return null;
  // Citation abbreviations ("Tex. Occ. Code", "Ohio Rev. Code").
  for (const [code, name] of Object.entries(STATE_NAMES)) {
    if (new RegExp(`\\b${name}\\b`, "i").test(haystack)) return code;
  }
  const abbr = haystack.match(/\b(Tex|Ohio|Cal|Fla|Ill|Mich|Penn|Ariz|Colo|Ga|Ind|Kan|Ky|La|Mass|Minn|Mo|Neb|Nev|Okla|Ore|Tenn|Va|Wash|Wis)\b/i);
  const ABBR_MAP: Record<string, string> = {
    tex: "TX", ohio: "OH", cal: "CA", fla: "FL", ill: "IL", mich: "MI", penn: "PA",
    ariz: "AZ", colo: "CO", ga: "GA", ind: "IN", kan: "KS", ky: "KY", la: "LA",
    mass: "MA", minn: "MN", mo: "MO", neb: "NE", nev: "NV", okla: "OK", ore: "OR",
    tenn: "TN", va: "VA", wash: "WA", wis: "WI",
  };
  return abbr ? (ABBR_MAP[abbr[1].toLowerCase()] ?? null) : null;
}

/** Locality (city) named by an authority, when it is a municipal rule. */
export function authorityLocality(row: AuthorityLike): string | null {
  const j = (row.jurisdiction ?? "").trim();
  if (!j) return null;
  const m = j.match(/\bcity of ([a-z .'-]+)/i);
  if (m) return m[1].trim();
  // "Houston, TX" style jurisdiction strings.
  const parts = j.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2 && toStateCode(parts[parts.length - 1])) {
    return parts.slice(0, -1).join(", ") || null;
  }
  return null;
}

function sameLocality(a: string, b: string): boolean {
  const n = (s: string) => s.toLowerCase().replace(/\b(city|town|village|of)\b/g, "").replace(/[^a-z]/g, "");
  return n(a) === n(b);
}

/**
 * Is a single authority applicable to the scope? Federal always; state must
 * match; local must match the locality when both are known; an unresolved
 * scope admits federal authorities only.
 */
export function isAuthorityApplicable(row: AuthorityLike, scope: JurisdictionScope): boolean {
  const authority = (row.citation ?? row.statute ?? "").trim();
  if (!authority) return false;
  if (isFederalAuthority(row)) return true;
  if (!scope.resolved || !scope.state) return false;

  const st = authorityState(row);
  if (st && st !== scope.state) return false;

  const locality = authorityLocality(row);
  if (locality && scope.locality && !sameLocality(locality, scope.locality)) return false;

  // No state signal on the row at all: it cannot be attributed to another
  // jurisdiction, so it stays with the resolved review state.
  return true;
}

export interface JurisdictionFilterResult<T> {
  /** Authorities applicable to the resolved jurisdiction. */
  applicable: T[];
  /** Authorities excluded because they belong elsewhere / scope unresolved. */
  excluded: T[];
  scope: JurisdictionScope;
  /** Mirror of `scope.resolved` for ergonomic call sites. */
  resolved: boolean;
}

export function filterAuthoritiesByJurisdiction<T extends AuthorityLike>(
  rows: readonly T[] | null | undefined,
  scope: JurisdictionScope,
): JurisdictionFilterResult<T> {
  const applicable: T[] = [];
  const excluded: T[] = [];
  for (const row of rows ?? []) {
    (isAuthorityApplicable(row, scope) ? applicable : excluded).push(row);
  }
  return { applicable, excluded, scope, resolved: scope.resolved };
}

/**
 * Chapter 05 relevance: ownership, lien, notice, title, abandonment,
 * release, or disposition. Routing happens BEFORE presentation — the
 * chapter never receives the full authority array.
 */
const OWNERSHIP_TOPICS = new Set([
  "abandonment",
  "lien",
  "notice",
  "owner_rights",
  "vendor_obligations",
  "timeline",
  "release",
  "auction_disposition",
]);

export function isOwnershipAuthority(row: AuthorityLike): boolean {
  const { topics } = classifyLegalTopics({
    title: row.title,
    topic: row.topic,
    summary: row.summary,
    citation: row.citation ?? row.statute,
    why_it_matters: row.why_it_matters,
    potential_negotiation_impact: row.potential_negotiation_impact,
  });
  if (topics.some((t) => OWNERSHIP_TOPICS.has(t))) return true;
  return /\b(title|ownership|possession|redeem|disposition)\b/i.test(
    `${row.title ?? ""} ${row.topic ?? ""} ${row.summary ?? ""}`,
  );
}

/** Chapter 05 input: jurisdiction-filtered AND ownership-relevant. */
export function selectOwnershipAuthorities<T extends AuthorityLike>(
  rows: readonly T[] | null | undefined,
  scope: JurisdictionScope,
): T[] {
  return filterAuthoritiesByJurisdiction(rows, scope).applicable.filter(isOwnershipAuthority);
}
